export type VercelPreviewTimelineItem = {
  user?: {
    login?: string | null;
  } | null;
  content?: string | null;
  timestamp?: string | null;
};

/** Stable prefix used by Vercel bot when posting markdown preview links. */
const VERCEL_PREVIEW_LINK_PREFIX = '[Visit Preview](';
// Preview deployments resolve on project subdomains under these suffixes.
// Intentionally excludes dashboard hosts like vercel.com.
const VERCEL_PREVIEW_ALLOWED_HOST_SUFFIXES = ['vercel.app', 'vercel.sh'] as const;

export type VercelPreviewResolution =
  | { kind: 'valid'; url: string }
  | { kind: 'invalid_candidate' }
  | { kind: 'no_candidate' };

/**
 * Extracts the URL target from a markdown link that starts with `prefix`.
 * Handles balanced parentheses so URLs like `https://x.dev/a(b)c` are parsed correctly.
 */
function extractMarkdownLinkTarget(markdown: string, prefix: string): string | null {
  const linkStartIndex = markdown.indexOf(prefix);
  if (linkStartIndex < 0) {
    return null;
  }

  // prefix ends with '(' — the open-parenthesis index is the last char of the match.
  const openParenIndex = linkStartIndex + prefix.length - 1;
  return extractMarkdownLinkTargetFromOpenParenthesis(markdown, openParenIndex);
}

function extractMarkdownLinkTargetFromOpenParenthesis(
  markdown: string,
  openParenthesisIndex: number,
): string | null {
  let nestedParentheses = 0;
  let target = '';

  for (let index = openParenthesisIndex + 1; index < markdown.length; index += 1) {
    const character = markdown[index];
    if (character === '(') {
      nestedParentheses += 1;
      target += character;
      continue;
    }

    if (character === ')') {
      if (nestedParentheses === 0) {
        return target;
      }
      nestedParentheses -= 1;
      target += character;
      continue;
    }

    target += character;
  }

  return null;
}

function extractMarkdownLinkTargets(markdown: string): string[] {
  const targets: string[] = [];

  for (let index = 0; index < markdown.length - 1; index += 1) {
    if (markdown[index] !== ']' || markdown[index + 1] !== '(') {
      continue;
    }

    const target = extractMarkdownLinkTargetFromOpenParenthesis(markdown, index + 1);
    if (target === null) {
      continue;
    }

    targets.push(target);
  }

  return targets;
}

function isAllowedVercelHostname(hostname: string): boolean {
  const normalizedHostname = hostname.toLowerCase();
  return VERCEL_PREVIEW_ALLOWED_HOST_SUFFIXES.some(
    // Require a deployment subdomain and reject apex hosts like vercel.app.
    (suffix) => normalizedHostname.endsWith(`.${suffix}`),
  );
}

function validateVercelPreviewUrl(candidate: string): string | null {
  const trimmedCandidate = candidate.trim();
  if (!trimmedCandidate) {
    return null;
  }

  try {
    const parsedUrl = new URL(trimmedCandidate);
    if (parsedUrl.protocol !== 'https:') {
      return null;
    }

    if (!isAllowedVercelHostname(parsedUrl.hostname)) {
      return null;
    }

    return trimmedCandidate;
  } catch {
    return null;
  }
}

function extractPreviewUrlCandidates(content: string): string[] {
  const candidates: string[] = [];

  const primaryCandidate = extractMarkdownLinkTarget(content, VERCEL_PREVIEW_LINK_PREFIX);
  if (primaryCandidate !== null) {
    candidates.push(primaryCandidate);
  }

  const fallbackCandidates = extractMarkdownLinkTargets(content);
  for (const candidate of fallbackCandidates) {
    if (candidates.includes(candidate)) {
      continue;
    }
    candidates.push(candidate);
  }

  return candidates;
}

function toTimestamp(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Resolves preview extraction state so callers can decide whether to clear, preserve, or update.
 */
export function resolveVercelPreviewUrl(
  timelineItems: ReadonlyArray<VercelPreviewTimelineItem> | null | undefined,
): VercelPreviewResolution {
  if (!Array.isArray(timelineItems)) {
    return { kind: 'no_candidate' };
  }

  const candidateComments = timelineItems
    .filter((item) => item.user?.login === 'vercel[bot]' && typeof item.content === 'string')
    .sort((a, b) => toTimestamp(b.timestamp) - toTimestamp(a.timestamp));

  let sawInvalidCandidate = false;

  for (const comment of candidateComments) {
    const candidates = extractPreviewUrlCandidates(comment.content ?? '');
    if (candidates.length === 0) {
      continue;
    }

    for (const candidate of candidates) {
      const validated = validateVercelPreviewUrl(candidate);
      if (validated) {
        return {
          kind: 'valid',
          url: validated,
        };
      }
    }

    sawInvalidCandidate = true;
  }

  return sawInvalidCandidate ? { kind: 'invalid_candidate' } : { kind: 'no_candidate' };
}

/**
 * Extracts the latest Vercel preview URL from discussion timeline bot comments.
 */
export function extractVercelPreviewUrl(
  timelineItems: ReadonlyArray<VercelPreviewTimelineItem> | null | undefined,
): string | null {
  const resolution = resolveVercelPreviewUrl(timelineItems);
  if (resolution.kind !== 'valid') {
    return null;
  }

  return resolution.url;
}
